import {
  FallOutlined,
  HeartOutlined,
  InboxOutlined,
  StopOutlined,
  UserDeleteOutlined
} from "@ant-design/icons"
import { Alert, App, Form, List, Modal, Radio, Typography } from "antd"
import React, { useEffect, useState, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { Archive } from "../../../Icon/icons/archive.tsx"
import {
  OverviewCardIcon
} from "../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import {
  LEAD_STATUSES,
  LEAD_STATUSES_MAP
} from "../../Lead/constants/lead-statuses.ts"
import { ARCHIVE_LEAD_STATUSES } from "../constants/archive-lead-allowed-statuses.ts"
import { lossReasonRules } from "../constants/loss-reason.ts"
import { LEADS_SAVE_RESULT } from "../constants/leads-save-results.ts"
import { archiveLead } from "../redux/leads.thunks.ts"
import type { PendingCommitment } from "../api/services.types.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"
import { PendingSignaturesAlert } from "../../Esign/components/PendingSignaturesAlert.tsx"
import {
  ARCHIVE_ACKNOWLEDGEMENT,
  pendingCommitmentsFrom
} from "../../Projects/api/pending-commitments.ts"
import type { ArchiveQuestion } from "../../Projects/api/services.types.ts"
import { archiveQuestionLabels } from "../../Projects/helpers/archive-question-labels.ts"

const { Text } = Typography

/** Purely decorative — keyed by the same status label used for `leadArchive.statuses.*`. */
const ARCHIVE_STATUS_ICONS: Record<string, ReactNode> = {
  Nurturing: <HeartOutlined />,
  Lost: <FallOutlined />,
  Unqualified: <UserDeleteOutlined />,
  "Do Not Contact": <StopOutlined />
}

interface ArchiveLeadFormData {
  target_status: number
  lost_reason?: string
  lost_notes?: string
}

interface PendingCommitmentsRejection {
  type?: string
  message?: string
}

/** What the dialog asks the server to do, without the lead it is about. */
export interface ArchiveLeadSubmission {
  target_status: number
  lost_reason?: string
  lost_notes: string
  cancel_appointments?: boolean
  void_signature_requests?: boolean
}

interface ArchiveLeadModalProps {
  leadId: string
  leadName?: string | null
  open: boolean
  onCancel: () => void
  onArchived?: () => void
  /** The outcomes offered; one is chosen for the user. Defaults to all four. */
  statuses?: readonly number[]
  /**
   * Sends the answers somewhere other than the archive endpoint (a status
   * suggestion's accept). Rejects with the same `pending_commitments` refusal
   * to raise the diary question.
   */
  submit?: (submission: ArchiveLeadSubmission) => Promise<unknown>
}

export function ArchiveLeadModal({
  leadId,
  leadName,
  open,
  onCancel,
  onArchived,
  statuses = ARCHIVE_LEAD_STATUSES,
  submit
}: ArchiveLeadModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const { formatDate } = useDateFormatter()
  const archiving = useAppSelector((state) => state.leads.saveFetching)
  const [submitting, setSubmitting] = useState(false)
  const saving = archiving || submitting
  const onlyStatus = statuses.length === 1 ? statuses[0] : undefined
  const [form] = Form.useForm<ArchiveLeadFormData>()
  const targetStatus = Form.useWatch("target_status", form)
  // Picking a different outcome asks the server a different question, so the
  // previous answer — and the confirmation riding on it — cannot carry over.
  // Without this, changing the radio after a refusal would send
  // `cancel_appointments` on the *first* request for the new outcome.
  useEffect(() => {
    setQuestion(null)
  }, [targetStatus])
  // Set when the server refuses because the lead still has meetings booked or
  // signature requests out. Holding them here is what turns the second click
  // into "yes, give them up" rather than a repeat of the declined request.
  const [question, setQuestion] = useState<ArchiveQuestion | null>(null)

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setQuestion(null)
    }
  }, [form, open])

  const archive = async (acknowledged: boolean) => {
    let values: ArchiveLeadFormData
    try {
      values = await form.validateFields()
    } catch {
      // Sharing the catch below would clear the meeting list being confirmed.
      return
    }

    try {
      // Only Lost carries a loss reason. Sending an empty one on the other
      // outcomes wipes whatever the lead already held.
      const lostReason =
        values.target_status === LEAD_STATUSES.LOST
          ? { lost_reason: values.lost_reason?.trim() }
          : {}
      const submission: ArchiveLeadSubmission = {
        target_status: values.target_status,
        ...lostReason,
        lost_notes: values.lost_notes?.trim() || "",
        ...(acknowledged ? ARCHIVE_ACKNOWLEDGEMENT : {})
      }
      if (submit) {
        setSubmitting(true)
        try {
          await submit(submission)
        } finally {
          setSubmitting(false)
        }
      } else {
        await dispatch(archiveLead({ id: leadId, ...submission })).unwrap()
      }
      form.resetFields()
      setQuestion(null)
      onArchived?.()
    } catch (rejection) {
      const pending = rejection as PendingCommitmentsRejection | undefined
      if (pending?.type !== LEADS_SAVE_RESULT.PENDING_COMMITMENTS) {
        // Anything else — a 500, a dropped connection, a missing field — has
        // already been reported by the thunk. Leaving confirm mode up would
        // pin the modal to a meeting list that no longer describes the state
        // and keep re-sending the confirmation.
        setQuestion(null)
        return
      }
      const asked = pendingCommitmentsFrom(pending)
      if (!asked) {
        // A refusal we cannot explain: the modal would render no alert, keep
        // its original footer, and re-send the identical refused request on
        // the next click. Say what happened instead of looking frozen.
        setQuestion(null)
        message.warning(
          pending.message || t("leadArchive.commitments.unavailable")
        )
        return
      }
      setQuestion(asked)
      return
    }
  }

  const displayName = leadName?.trim() || t("leadArchive.thisLead")
  const commitments: PendingCommitment[] = question?.pendingCommitments ?? []
  const hasCommitments = commitments.length > 0
  const labels = question ? archiveQuestionLabels(question, "leadArchive", t) : null

  return (
    <Modal
      open={open}
      title={
        // The close control is absolutely positioned over the header's right edge.
        <div className="flex items-center gap-3 pr-6">
          <OverviewCardIcon icon={<Archive />} accent="orange" size={40} />
          <div className="flex flex-col">
            <span>{t("leadArchive.title", { name: displayName })}</span>
            <Text type="secondary" className="text-sm font-normal">
              {t("leadArchive.subtitle")}
            </Text>
          </div>
        </div>
      }
      okText={labels ? labels.confirm : t("leadArchive.confirm")}
      cancelText={
        labels
          ? t("leadArchive.commitments.keepActive")
          : t("leadArchive.cancel")
      }
      okButtonProps={{ danger: true }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => archive(!!question)}
      onCancel={onCancel}
      destroyOnHidden
      footer={(_, { OkBtn, CancelBtn }) => (
        // Buttons never shrink below their labels, so the confirmation pair
        // overflows the dialog unless the row may wrap.
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text type="secondary" className="flex items-center gap-1.5 text-xs">
            <InboxOutlined />
            {t("leadArchive.restoreNotice")}
          </Text>
          <div className="flex grow flex-wrap justify-end gap-2">
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message={t("leadArchive.impact")}
      />

      {hasCommitments && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          data-testid="archive-pending-commitments"
          message={t("leadArchive.commitments.title")}
          description={
            <>
              <div className="mb-2">
                {t("leadArchive.commitments.body", {
                  count: commitments.length
                })}
              </div>
              <List
                size="small"
                dataSource={commitments}
                renderItem={(commitment) => (
                  <List.Item className="!px-0">
                    <div className="flex flex-col">
                      <Text strong>{commitment.title}</Text>
                      <Text type="secondary" className="text-xs">
                        {formatDate(commitment.start_at)}
                        {commitment.has_external_invitees
                          ? ` · ${t("leadArchive.commitments.externalInvitee")}`
                          : ""}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </>
          }
        />
      )}

      {question?.pendingSignatures.length ? (
        <PendingSignaturesAlert signatures={question.pendingSignatures} />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        disabled={saving}
        initialValues={
          onlyStatus === undefined ? undefined : { target_status: onlyStatus }
        }
      >
        <Form.Item<ArchiveLeadFormData>
          name="target_status"
          label={t("leadArchive.targetStatus")}
          rules={[
            {
              required: true,
              message: t("leadArchive.targetStatusRequired")
            }
          ]}
        >
          <Radio.Group className="w-full">
            <div className="grid grid-cols-2 gap-3">
              {statuses.map((status) => {
                const label =
                  LEAD_STATUSES_MAP[status as keyof typeof LEAD_STATUSES_MAP]
                    .label
                const descriptionId = `archive-lead-status-description-${status}`
                return (
                  <Radio
                    key={status}
                    value={status}
                    aria-label={t(`leadArchive.statuses.${label}`)}
                    aria-describedby={descriptionId}
                    className="!m-0 items-start rounded-lg border border-gray-200 p-3 [&.ant-radio-wrapper-checked]:border-teal-600 [&.ant-radio-wrapper-checked]:bg-teal-50"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">
                          {ARCHIVE_STATUS_ICONS[label]}
                        </span>
                        <Text strong>{t(`leadArchive.statuses.${label}`)}</Text>
                      </div>
                      <Text type="secondary" className="text-xs" id={descriptionId}>
                        {t(`leadArchive.statusDescriptions.${label}`)}
                      </Text>
                    </div>
                  </Radio>
                )
              })}
            </div>
          </Radio.Group>
        </Form.Item>

        {targetStatus === LEAD_STATUSES.LOST && (
          <Form.Item<ArchiveLeadFormData>
            name="lost_reason"
            label={t("leadArchive.lossReason")}
            preserve={false}
            rules={lossReasonRules(t("leadArchive.lossReasonRequired"))}
          >
            <TextArea
              rows={3}
              placeholder={t("leadArchive.lossReasonPlaceholder")}
            />
          </Form.Item>
        )}

        <Form.Item<ArchiveLeadFormData>
          name="lost_notes"
          label={t("leadArchive.notes")}
          // The counter is absolutely positioned a line height below the field,
          // which is more than the app's 16px form-item margin leaves.
          className="pb-4"
        >
          <TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder={t("leadArchive.notesPlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
